import type {
  ActivityDay,
  ActivityDayTotal,
  ActivityEntry,
  ApiToken,
  ApiTokenScope,
  AuthResponse,
  CreatedApiToken,
  CreateEntryInput,
  CreateFoodInput,
  CreateRecipeInput,
  CreateWeightInput,
  DaySummary,
  DayTotals,
  DiaryEntry,
  DiaryStats,
  DishEstimate,
  Food,
  LabelScanResult,
  MealType,
  ParseResult,
  PhotoScanResult,
  PublicUser,
  QuantityInput,
  RecentResult,
  Recipe,
  WaterDay,
  WaterEntry,
  WeightEntry,
  WeightSeries,
  TdeeEstimate,
} from '@nutrigeist/shared';
import { submitWrite } from '../offline/queue.js';
import { apiDownload, apiRequest } from './client.js';

/**
 * Typed wrappers around the REST API.
 *
 * Request and response types come from `@nutrigeist/shared`, the same module
 * the server validates against, so a contract change fails the web build rather
 * than surfacing as a runtime surprise on a phone.
 */

// --- auth -------------------------------------------------------------------

export const authApi = {
  register: (input: { email: string; password: string; displayName: string }) =>
    apiRequest<AuthResponse>('/api/auth/register', { body: input, anonymous: true }),

  login: (input: { email: string; password: string }) =>
    apiRequest<AuthResponse>('/api/auth/login', { body: input, anonymous: true }),

  logout: (refreshToken: string) =>
    apiRequest<void>('/api/auth/logout', { body: { refreshToken } }),

  me: () => apiRequest<PublicUser>('/api/auth/me'),

  updateProfile: (input: {
    displayName?: string;
    goals?: Partial<PublicUser['goals']>;
    preferences?: Partial<PublicUser['preferences']>;
  }) => apiRequest<PublicUser>('/api/users/me', { method: 'PATCH', body: input }),

  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    apiRequest<void>('/api/users/me/password', { body: input }),
};

// --- weight -----------------------------------------------------------------

export const weightApi = {
  list: (query: { from?: string; to?: string; limit?: number } = {}) =>
    apiRequest<{ entries: WeightEntry[] }>('/api/weights', { query }),

  series: (query: { from?: string; to?: string; trendWindow?: number } = {}) =>
    apiRequest<WeightSeries>('/api/weights/series', { query }),

  create: (input: CreateWeightInput & { measuredAt?: string | Date }) =>
    submitWrite<WeightEntry>(input as Record<string, unknown>, {
      kind: 'weight',
      path: '/api/weights',
      label: `${input.weightKg} kg`,
    }),

  update: (
    id: string,
    input: {
      weightKg?: number;
      bodyFatPercent?: number | null;
      measuredAt?: string;
      note?: string | null;
    },
  ) => apiRequest<WeightEntry>(`/api/weights/${id}`, { method: 'PATCH', body: input }),

  remove: (id: string) => apiRequest<void>(`/api/weights/${id}`, { method: 'DELETE' }),
};

// --- foods ------------------------------------------------------------------

export const foodApi = {
  search: (q: string, options: { limit?: number; remote?: boolean } = {}) =>
    apiRequest<{ results: { food: Food; score: number }[] }>('/api/foods/search', {
      query: { q, limit: options.limit, remote: options.remote },
    }),

  byBarcode: (barcode: string) => apiRequest<Food>(`/api/foods/barcode/${barcode}`),

  byId: (id: string) => apiRequest<Food>(`/api/foods/${id}`),

  create: (input: CreateFoodInput) => apiRequest<Food>('/api/foods', { body: input }),

  update: (id: string, input: Partial<CreateFoodInput>) =>
    apiRequest<Food>(`/api/foods/${id}`, { method: 'PATCH', body: input }),

  remove: (id: string) => apiRequest<void>(`/api/foods/${id}`, { method: 'DELETE' }),
};

// --- diary ------------------------------------------------------------------

export const diaryApi = {
  day: (query: { date?: string; timezone?: string } = {}) =>
    apiRequest<DaySummary>('/api/diary/day', { query }),

  range: (query: { from: string; to: string; timezone?: string }) =>
    apiRequest<{ days: DayTotals[] }>('/api/diary/range', { query }),

  recent: (days = 7) => apiRequest<RecentResult>('/api/diary/recent', { query: { days } }),

  stats: (days = 90) => apiRequest<DiaryStats>('/api/diary/stats', { query: { days } }),

  /**
   * Logging food is the one write most likely to happen without signal — in a
   * shop, in a canteen basement — so it goes through the queue rather than
   * straight out. Returns `queued: true` when it was kept for later.
   */
  createEntries: (entries: CreateEntryInput[], label: string) =>
    submitWrite<{ entries: DiaryEntry[] }>(
      { entries },
      { kind: 'diary', path: '/api/diary/entries', label },
    ),

  updateEntry: (
    id: string,
    input: {
      quantity?: QuantityInput;
      mealType?: MealType;
      loggedAt?: string;
      note?: string | null;
    },
  ) => apiRequest<DiaryEntry>(`/api/diary/entries/${id}`, { method: 'PATCH', body: input }),

  deleteEntry: (id: string) => apiRequest<void>(`/api/diary/entries/${id}`, { method: 'DELETE' }),

  copy: (input: { entryIds: string[]; date?: string; timezone?: string; mealType?: MealType }) =>
    apiRequest<{ entries: DiaryEntry[] }>('/api/diary/copy', { body: input }),
};

// --- water ------------------------------------------------------------------

export const waterApi = {
  day: (query: { date?: string; timezone?: string } = {}) =>
    apiRequest<WaterDay>('/api/water/day', { query }),

  add: (input: { ml: number; loggedAt?: string }) =>
    submitWrite<WaterEntry>(input, {
      kind: 'water',
      path: '/api/water',
      label: `${input.ml} ml`,
    }),

  remove: (id: string) => apiRequest<void>(`/api/water/${id}`, { method: 'DELETE' }),
};

// --- recipes ----------------------------------------------------------------

export const recipeApi = {
  list: () => apiRequest<{ recipes: Recipe[] }>('/api/recipes'),

  byId: (id: string) => apiRequest<Recipe>(`/api/recipes/${id}`),

  create: (input: CreateRecipeInput) => apiRequest<Recipe>('/api/recipes', { body: input }),

  update: (id: string, input: Partial<CreateRecipeInput>) =>
    apiRequest<Recipe>(`/api/recipes/${id}`, { method: 'PATCH', body: input }),

  remove: (id: string) => apiRequest<void>(`/api/recipes/${id}`, { method: 'DELETE' }),
};

// --- activity ---------------------------------------------------------------

export const activityApi = {
  day: (query: { date?: string; timezone?: string } = {}) =>
    apiRequest<ActivityDay>('/api/activity/day', { query }),

  range: (query: { from: string; to: string; timezone?: string }) =>
    apiRequest<{ days: ActivityDayTotal[] }>('/api/activity/range', { query }),

  add: (input: {
    date?: string;
    activeKcal: number;
    steps?: number | null;
    label?: string | null;
    source?: 'manual' | 'health_connect' | 'import';
  }) =>
    submitWrite<ActivityEntry>(input, {
      kind: 'activity',
      path: '/api/activity',
      label: `${Math.round(input.activeKcal)} kcal`,
    }),

  remove: (id: string) => apiRequest<void>(`/api/activity/${id}`, { method: 'DELETE' }),
};

// --- adaptive TDEE ----------------------------------------------------------

export const tdeeApi = {
  estimate: (days = 28) => apiRequest<TdeeEstimate>('/api/tdee', { query: { days } }),
};

// --- api tokens -------------------------------------------------------------

export const tokenApi = {
  list: () => apiRequest<{ tokens: ApiToken[] }>('/api/tokens'),

  create: (input: { name: string; scopes: ApiTokenScope[]; expiresInDays?: number | null }) =>
    apiRequest<CreatedApiToken>('/api/tokens', { body: input }),

  revoke: (id: string) => apiRequest<void>(`/api/tokens/${id}`, { method: 'DELETE' }),
};

// --- export -----------------------------------------------------------------

export const exportApi = {
  archive: () => apiDownload('/api/export', 'nutrigeist-backup.json'),
  diaryCsv: () => apiDownload('/api/export/diary.csv', 'nutrigeist-tagebuch.csv'),
  weightCsv: () => apiDownload('/api/export/weights.csv', 'nutrigeist-gewicht.csv'),
};

// --- intake (free text + label scan) ----------------------------------------

export const intakeApi = {
  parseText: (input: { text: string; allowEstimates?: boolean }) =>
    apiRequest<ParseResult>('/api/intake/parse', { body: input }),

  estimateDish: (input: { name: string; servings?: number }) =>
    apiRequest<DishEstimate>('/api/intake/dish', { body: input }),

  scanLabel: (image: Blob, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append('image', image, 'label.jpg');
    return apiRequest<LabelScanResult>('/api/intake/label-scan', { formData, signal });
  },

  scanPhoto: (image: Blob, signal?: AbortSignal) => {
    const formData = new FormData();
    formData.append('image', image, 'meal.jpg');
    return apiRequest<PhotoScanResult>('/api/intake/photo-scan', { formData, signal });
  },

  visionHealth: () =>
    apiRequest<{
      /** The model that would serve a scan right now. */
      model: string;
      reachable: boolean;
      /** Which provider that would be, or null when neither can answer. */
      provider: 'ollama' | 'openrouter' | null;
      /** Per-node Ollama detail. Empty for non-admins: these are LAN addresses. */
      nodes: { endpoint: string; reachable: boolean; hasModel: boolean; models: string[] }[];
      fallback: {
        configured: boolean;
        model: string | null;
        reachable: boolean;
        error: string | null;
      };
    }>('/api/intake/vision/health'),
};
